import jsPDF from 'jspdf';
import { loadCompanyIdentity, formatCompanyTaxId } from '../features/attendanceEnhancements/companyIdentity';
import { PEDAGOGICAL_EVALUATION_SECTIONS, PEDAGOGICAL_AXES, WEEK_DAYS } from './studentPedagogicalModels';

const BLUE = [18, 62, 115];
const TEXT = [30, 41, 59];
const MUTED = [100, 116, 139];
const LIGHT = [245, 248, 252];
const BORDER = [203, 213, 225];
const GREEN = [236, 247, 226];

const clean = (value) => String(value ?? '').trim();
const compact = (values) => values.map(clean).filter(Boolean);
const formatDate = (value) => {
  const match = clean(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : clean(value);
};
const age = (value) => {
  if (!value) return '';
  const birth = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(birth.getTime())) return '';
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  const birthday = new Date(now.getFullYear(), birth.getMonth(), birth.getDate());
  if (now < birthday) years -= 1;
  return years >= 0 ? String(years) : '';
};
const safeFilename = (value) => clean(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'documento.pdf';

const orgLines = (org) => {
  const tax = formatCompanyTaxId(org?.taxId);
  const contact = compact([
    tax ? `CNPJ ${tax}` : '',
    org?.phone ? `Tel. ${org.phone}` : '',
    org?.email,
    org?.website,
  ]).join(' • ');
  const address = compact([
    org?.addressLine1,
    org?.addressLine2,
    compact([org?.city, org?.state]).join(' / '),
    org?.zipCode,
  ]).join(' • ');
  return {
    name: clean(org?.tradeName) || clean(org?.name) || 'Organização',
    legal: clean(org?.name),
    contact,
    address,
  };
};

const drawLogo = (doc, dataUrl, x, y, size) => {
  if (!dataUrl) return false;
  try {
    const format = /^data:image\/png/i.test(dataUrl) ? 'PNG' : 'JPEG';
    doc.addImage(dataUrl, format, x, y, size, size);
    return true;
  } catch {
    return false;
  }
};

class PdfComposer {
  constructor({ title, subtitle, organization, student, klass, responsibleName }) {
    this.doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.margin = 12;
    this.footerY = this.pageHeight - 10;
    this.title = title;
    this.subtitle = subtitle;
    this.organization = organization || {};
    this.student = student || null;
    this.klass = klass || null;
    this.responsibleName = responsibleName || '';
    this.org = orgLines(this.organization);
    this.y = 0;
    this.pageCount = 0;
    this.startPage(true);
  }

  startPage(first = false) {
    if (!first) this.doc.addPage();
    this.pageCount += 1;
    const doc = this.doc;
    doc.setFillColor(...BLUE);
    doc.rect(0, 0, this.pageWidth, 34, 'F');
    const logoOk = drawLogo(doc, this.organization.logoDataUrl, this.margin, 6, 21, 21);
    if (!logoOk) {
      doc.setFillColor(255, 255, 255);
      doc.circle(this.margin + 10, 16.5, 8, 'F');
      doc.setTextColor(...BLUE);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text('iBPM', this.margin + 10, 18.5, { align: 'center' });
    }
    const x = this.margin + 27;
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12.5);
    doc.text(this.title.toUpperCase(), x, 9.5, { maxWidth: this.pageWidth - x - this.margin });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.7);
    if (this.subtitle) doc.text(this.subtitle, x, 14, { maxWidth: this.pageWidth - x - this.margin });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text(this.org.name, x, 19.5, { maxWidth: this.pageWidth - x - this.margin });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.2);
    const identity = compact([
      this.org.legal && this.org.legal !== this.org.name ? this.org.legal : '',
      this.org.contact,
      this.org.address,
    ]).join(' • ');
    if (identity) doc.text(identity, x, 24, { maxWidth: this.pageWidth - x - this.margin });

    this.y = 39;
    if (this.student) this.studentHeader();
    else if (this.klass) this.classHeader();
  }

  studentHeader() {
    const student = this.student;
    const klass = this.klass || {};
    const rows = [
      ['Aluno', student?.fullName || student?.socialName || '-', 'RA', student?.ra || '-'],
      ['Matrícula', student?.registrationNumber || '-', 'Turma', klass?.name || '-'],
      ['Nascimento', formatDate(student?.birthDate) || '-', 'Idade', age(student?.birthDate) ? `${age(student?.birthDate)} ano(s)` : '-'],
      ['Período', klass?.period || '-', 'Responsável', this.responsibleName || '-'],
    ];
    this.keyValueTable(rows);
  }

  classHeader() {
    const klass = this.klass || {};
    this.keyValueTable([
      ['Turma', klass?.name || '-', 'Período', klass?.period || '-'],
      ['Responsável', this.responsibleName || '-', '', ''],
    ]);
  }

  keyValueTable(rows) {
    const doc = this.doc;
    const width = this.pageWidth - this.margin * 2;
    const colW = width / 4;
    rows.forEach((row) => {
      this.ensure(8);
      for (let i = 0; i < 4; i += 1) {
        const x = this.margin + colW * i;
        doc.setDrawColor(...BORDER);
        doc.setFillColor(i % 2 === 0 ? 241 : 255, i % 2 === 0 ? 245 : 255, i % 2 === 0 ? 249 : 255);
        doc.rect(x, this.y, colW, 7, 'FD');
        doc.setTextColor(...(i % 2 === 0 ? MUTED : TEXT));
        doc.setFont('helvetica', i % 2 === 0 ? 'bold' : 'normal');
        doc.setFontSize(6.4);
        doc.text(clean(row[i]) || '-', x + 1.5, this.y + 4.5, { maxWidth: colW - 3 });
      }
      this.y += 7;
    });
    this.y += 4;
  }

  ensure(height = 12) {
    if (this.y + height > this.footerY - 7) this.startPage(false);
  }

  section(title, tone = 'blue') {
    this.ensure(10);
    const doc = this.doc;
    const fill = tone === 'green' ? GREEN : [235, 242, 252];
    doc.setFillColor(...fill);
    doc.setDrawColor(...BORDER);
    doc.roundedRect(this.margin, this.y, this.pageWidth - this.margin * 2, 8, 1.5, 1.5, 'FD');
    doc.setTextColor(...BLUE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text(clean(title), this.margin + 2.5, this.y + 5.2);
    this.y += 11;
  }

  paragraph(label, value, minHeight = 12) {
    const doc = this.doc;
    const width = this.pageWidth - this.margin * 2;
    const body = clean(value) || '—';
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.4);
    const lines = doc.splitTextToSize(body, width - 5);
    const height = Math.max(minHeight, lines.length * 3.4 + 8);
    this.ensure(height + 2);
    doc.setDrawColor(...BORDER);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(this.margin, this.y, width, height, 1.5, 1.5, 'FD');
    doc.setTextColor(...MUTED);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.4);
    doc.text(clean(label).toUpperCase(), this.margin + 2.5, this.y + 4);
    doc.setTextColor(...TEXT);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.4);
    doc.text(lines, this.margin + 2.5, this.y + 8);
    this.y += height + 3;
  }

  compactFields(fields, columns = 2) {
    const doc = this.doc;
    const gap = 2.5;
    const width = (this.pageWidth - this.margin * 2 - gap * (columns - 1)) / columns;
    let index = 0;
    while (index < fields.length) {
      const row = fields.slice(index, index + columns);
      const lineSets = row.map(([, value]) => doc.splitTextToSize(clean(value) || '—', width - 4));
      const height = Math.max(10, ...lineSets.map((lines) => lines.length * 3.2 + 7));
      this.ensure(height + 2);
      row.forEach(([label], col) => {
        const x = this.margin + col * (width + gap);
        doc.setDrawColor(...BORDER);
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x, this.y, width, height, 1.5, 1.5, 'FD');
        doc.setTextColor(...MUTED);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6);
        doc.text(clean(label).toUpperCase(), x + 2, this.y + 3.5);
        doc.setTextColor(...TEXT);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.1);
        doc.text(lineSets[col], x + 2, this.y + 7);
      });
      this.y += height + 2.5;
      index += columns;
    }
  }

  evaluationRows(rows) {
    const doc = this.doc;
    const total = this.pageWidth - this.margin * 2;
    const itemW = total * 0.56;
    const statusW = total * 0.12;
    const helpW = total - itemW - statusW;

    const header = () => {
      this.ensure(9);
      let x = this.margin;
      [['Item avaliado', itemW], ['Aval.', statusW], ['Tipos de ajuda', helpW]].forEach(([label, width]) => {
        doc.setFillColor(...BLUE);
        doc.setDrawColor(...BLUE);
        doc.rect(x, this.y, width, 7, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.2);
        doc.text(label, x + 1.5, this.y + 4.7, { maxWidth: width - 3 });
        x += width;
      });
      this.y += 7;
    };

    header();
    rows.forEach((row) => {
      const itemText = compact([
        row.label,
        row.choices?.length ? `Marcado: ${row.choices.join(', ')}` : '',
        row.extra ? `${row.extraLabel || 'Complemento'}: ${row.extra}` : '',
      ]).join(' | ');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.3);
      const itemLines = doc.splitTextToSize(itemText || '—', itemW - 3);
      const helpLines = doc.splitTextToSize(clean(row.help) || '—', helpW - 3);
      const height = Math.max(7, Math.max(itemLines.length, helpLines.length) * 3 + 3);
      if (this.y + height > this.footerY - 7) {
        this.startPage(false);
        header();
      }
      let x = this.margin;
      [
        [itemLines, itemW, 'left'],
        [[clean(row.status) || '—'], statusW, 'center'],
        [helpLines, helpW, 'left'],
      ].forEach(([lines, width, align]) => {
        doc.setDrawColor(...BORDER);
        doc.setFillColor(255, 255, 255);
        doc.rect(x, this.y, width, height, 'FD');
        doc.setTextColor(...TEXT);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.3);
        const tx = align === 'center' ? x + width / 2 : x + 1.5;
        doc.text(lines, tx, this.y + 4.2, { align });
        x += width;
      });
      this.y += height;
    });
    this.y += 3;
  }

  footerAll() {
    const doc = this.doc;
    const total = doc.getNumberOfPages();
    for (let page = 1; page <= total; page += 1) {
      doc.setPage(page);
      doc.setDrawColor(...BORDER);
      doc.line(this.margin, this.footerY - 4, this.pageWidth - this.margin, this.footerY - 4);
      doc.setTextColor(...MUTED);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.4);
      doc.text('Documento gerado pelo sistema Intelligent BPM Nash Tecnologia.', this.pageWidth / 2, this.footerY, { align: 'center' });
      doc.text(`Página ${page} de ${total}`, this.pageWidth - this.margin, this.footerY, { align: 'right' });
    }
  }
}

const evaluationRowsFor = (data, section) => section.groups.flatMap((group) =>
  group.items.map((item) => {
    const value = data?.sections?.[section.key]?.items?.[item.key] || {};
    return {
      label: `${group.title} - ${item.label}`,
      status: value.status || '',
      help: value.help || '',
      choices: Array.isArray(value.choices) ? value.choices : [],
      extra: value.extra || '',
      extraLabel: item.extraLabel,
    };
  })
);

const downloadPlanning = async ({ data, organization, klass, responsibleName }) => {
  const pdf = new PdfComposer({
    title: 'Planejamento Pedagógico Dinâmico',
    subtitle: 'Planejamento semanal e diário',
    organization,
    klass,
    responsibleName: data.teacherName || responsibleName,
  });
  pdf.section('Identificação do planejamento');
  pdf.compactFields([
    ['Mês', data.month],
    ['Ano', data.year],
    ['Semana de', formatDate(data.weekStart)],
    ['Semana até', formatDate(data.weekEnd)],
    ['Profissional', data.teacherName || responsibleName],
  ]);
  pdf.paragraph('Resumo / bilhete pedagógico', data.pedagogicalSummary, 16);
  pdf.section('Eixos trabalhados', 'green');
  pdf.paragraph('Selecionados', PEDAGOGICAL_AXES.filter((axis) => data.axes?.[axis]).join(', ') || 'Nenhum eixo marcado', 11);
  pdf.section('Resumo semanal');
  WEEK_DAYS.forEach(([key, label]) => pdf.paragraph(label, data.weekly?.[key]?.proposal, 10));

  WEEK_DAYS.forEach(([key, label]) => {
    const day = data.days?.[key] || {};
    pdf.startPage(false);
    pdf.section(`${label} ${day.date ? '- ' + formatDate(day.date) : ''}`);
    pdf.compactFields([
      ['Acolhimento', day.arrivalTime],
      ['Café da manhã', day.breakfastTime],
      ['Higiene / escovação', day.hygieneTime],
      ['Almoço', day.lunchTime],
      ['Água / banheiro', day.waterBathroomTime],
      ['Soninho / saída', day.sleepTime],
    ]);
    [
      ['Chamada', day.call],
      ['Roda de conversa / músicas', day.conversationMusic],
      ['Calendário', day.calendar],
      ['Escolha do ajudante do dia', day.helperOfDay],
    ].forEach(([title, block]) => {
      pdf.section(title, 'green');
      pdf.compactFields([
        ['Espaço', block?.space],
        ['Área de conhecimento', block?.knowledgeArea],
        ['Eixo', block?.axis],
      ]);
      pdf.paragraph('Desenvolvimento', block?.development, 11);
    });
    [
      ['Atividade principal', day.mainActivity],
      ['Atividade secundária', day.secondaryActivity],
    ].forEach(([title, block]) => {
      pdf.section(title);
      pdf.compactFields([
        ...(title === 'Atividade secundária' ? [['Horário', block?.time]] : []),
        ['Tema', block?.theme],
        ['Espaço', block?.space],
        ['Área de conhecimento', block?.knowledgeArea],
        ['Eixo(s)', block?.axis],
        ['Metas', block?.goals],
        ['Objetivos', block?.objectives],
        ['Conteúdos', block?.contents],
      ]);
      pdf.paragraph('Desenvolvimento', block?.development, 15);
      pdf.paragraph('Recursos', block?.resources, 10);
    });
    pdf.section('Observação / Avaliação', 'green');
    pdf.paragraph('Registro', day.observationEvaluation, 22);
  });

  pdf.footerAll();
  pdf.doc.save(safeFilename(`planejamento-pedagogico-${data.weekStart || 'semana'}.pdf`));
};

const downloadReferral = async ({ data, organization, student, klass, responsibleName }) => {
  const pdf = new PdfComposer({
    title: 'Encaminhamento para Serviços de Educação Especial',
    subtitle: 'Formulário pedagógico',
    organization,
    student,
    klass,
    responsibleName: data.teacherName || responsibleName,
  });
  pdf.section('Identificação');
  pdf.compactFields([
    ['Unidade Escolar', orgLines(organization).name],
    ['Telefone', organization?.phone],
    ['Data', formatDate(data.date)],
    ['Professor da classe comum', data.teacherName || responsibleName],
  ]);
  pdf.section('1. Critérios de elegibilidade', 'green');
  const eligibility = [
    ['hearing', 'Estudante com Deficiência Auditiva'],
    ['visual', 'Estudante com Deficiência Visual'],
    ['physical', 'Estudante com Deficiência Física'],
    ['intellectual', 'Estudante com Deficiência Intelectual'],
    ['multiple', 'Estudante com Deficiência Múltipla'],
    ['autism', 'Estudante com Transtorno Global do Desenvolvimento / Transtorno do Espectro do Autismo'],
    ['giftedness', 'Estudante com Altas Habilidades / Superdotação'],
    ['specialNeeds', 'Estudante com Necessidades Educacionais Especiais'],
  ];
  pdf.paragraph('Critérios marcados', compact([
    ...eligibility.filter(([key]) => data.eligibility?.[key]).map(([, label]) => label),
    data.otherEligibility ? `Outros: ${data.otherEligibility}` : '',
  ]).join('\n') || 'Nenhum critério marcado', 24);
  pdf.section('2. Breve histórico do estudante', 'green');
  pdf.paragraph('Síntese dos dados coletados com a família', data.studentHistory, 36);
  pdf.section('3. Motivo do encaminhamento', 'green');
  pdf.paragraph('Síntese da avaliação do professor da classe comum', data.referralReason, 36);
  pdf.section('4. Providências já tomadas', 'green');
  pdf.paragraph('Providências para atender às necessidades educacionais apresentadas', data.measuresTaken, 32);
  pdf.section('5. Assinaturas', 'green');
  pdf.compactFields([
    ['Pai ou responsável legal', data.signatures?.guardian],
    ['Diretor da Unidade Escolar', data.signatures?.director],
    ['Professor responsável pelo encaminhamento', data.signatures?.teacher || data.teacherName || responsibleName],
    ['Data', formatDate(data.date)],
  ], 1);
  pdf.footerAll();
  pdf.doc.save(safeFilename(`encaminhamento-educacao-especial-${student?.fullName || 'aluno'}.pdf`));
};

const downloadEvaluation = async ({ data, organization, student, klass, responsibleName }) => {
  const pdf = new PdfComposer({
    title: 'Planilha de Avaliação Pedagógica',
    subtitle: 'Educação Infantil - Registro das condições de aprendizagem',
    organization,
    student,
    klass,
    responsibleName: data.teacherName || responsibleName,
  });
  pdf.section('Identificação');
  pdf.compactFields([
    ['Unidade Escolar', orgLines(organization).name],
    ['Data', formatDate(data.date)],
    ['Professor de Classe Comum', data.teacherName || responsibleName],
    ['Data do encaminhamento', formatDate(data.referralDate)],
  ]);
  pdf.section('Legenda', 'green');
  pdf.paragraph('Critérios', 'D - Desenvolvido | ED - Em Desenvolvimento | ND - Não Desenvolvido | NA - Não se Aplica', 10);

  PEDAGOGICAL_EVALUATION_SECTIONS.forEach((section) => {
    pdf.section(section.title, 'green');
    pdf.evaluationRows(evaluationRowsFor(data, section));
    pdf.paragraph('Observações', data.sections?.[section.key]?.observations, 18);
  });

  pdf.section('Assinaturas', 'green');
  pdf.compactFields([
    ['Diretor da Unidade Escolar', data.signatures?.director],
    ['Professor de Classe Comum', data.signatures?.teacher || data.teacherName || responsibleName],
    ['Data', formatDate(data.date)],
  ], 1);
  pdf.footerAll();
  pdf.doc.save(safeFilename(`avaliacao-pedagogica-${student?.fullName || 'aluno'}.pdf`));
};

export async function downloadStudentPedagogicalPdf({
  type,
  data,
  student,
  klass,
  responsibleName = '',
}) {
  const organization = await loadCompanyIdentity();
  if (type === 'PLANNING') return downloadPlanning({ data, organization, klass, responsibleName });
  if (type === 'SPECIAL_EDUCATION_REFERRAL') return downloadReferral({ data, organization, student, klass, responsibleName });
  return downloadEvaluation({ data, organization, student, klass, responsibleName });
}
